import { unwrapResult } from "@reduxjs/toolkit";
import { useCallClient } from "components/remoteAccess/callClient";
import { CallClientId, CallClientOptions, CallRef } from "components/remoteAccess/helpers";
import { RemoteAccessButtons } from "components/remoteAccess/RemoteAccessButtons";
import { RemoteAccessConsent } from "components/remoteAccess/RemoteAccessConsent";
import { RemoteSessionEnded } from "components/remoteAccess/RemoteSessionEnded";
import { RemoteShareCamera } from "components/remoteAccess/RemoteShareCamera";
import { RemoteViewCamera } from "components/remoteAccess/RemoteViewCamera";
import { isAbortError, isEmpty } from "helpers";
import { nanoid } from "nanoid";
import { FileType } from "operations/schema/schema";
import Peer, { MediaConnection } from "peerjs";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { IntlShape, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  addFile,
  getFiles,
  selectSelectedJob,
  selectSelectedJobVisit,
  uploadFile,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import styles from "./RemoteAccess.module.css";

interface RemoteAccessProps {
  isSharing: boolean;
  onEnd: () => void;
  onStartWork: () => void;
}

export const RemoteAccess: FC<RemoteAccessProps> = (props) => {
  const { isSharing, onEnd, onStartWork } = props;
  const dispatch = useAppDispatch();
  const remoteCallRef = useRef<CallRef>(null);
  const job = useAppSelector(selectSelectedJob);
  const { workTimes } = useAppSelector(selectSelectedJobVisit);
  const { autoSyncDocuments } = useAppSelector((s) => s.user);
  const [isSessionEnded, setIsSessionEnded] = useState<boolean>(false);
  const [callActive, setCallActive] = useState<boolean>(false);
  const [eventState, setEventState] = useState<number>(0);
  const intl = useIntl();

  const workStarted = !isEmpty(workTimes) && workTimes[0].startTime !== null;

  /*events:  0 = nothing,
		1 = streaming success
		2 = sucess streaming sent*/

  useEffect(() => {
    if (eventState === 1) {
      setEventState(2); //success streaming sent
    }
  }, [eventState]);

  const handleEndCall = useCallback(() => {
    setIsSessionEnded(true);
    onEnd();
  }, [onEnd]);

  const onCapture = useCallback(() => {
    if (!remoteCallRef.current) return;

    const data = remoteCallRef.current.capture();
    if (job && data) {
      console.log(data);
      getCaptureFilename(data, intl)
        .then(async (filename) => {
          if (!data) return;
          let dataStart = data.indexOf("base64,");
          const f: FileType = {
            id: nanoid(),
            name: filename,
            data: data.substring(dataStart + 7),
            description: "",
            size: data.length,
            isFullSize: true,
            fileType: "jpeg",
            useInServiceReport: false,
          };
          if (autoSyncDocuments) {
            dispatch(uploadFile({ jobId: job.id, file: f }))
              .then(unwrapResult)
              .then(() => {
                dispatch(getFiles());
              })
              .catch((error: any) => {
                if (isAbortError(error)) return;
                dispatch(addSnackbarMessage({ key: "UploadFile-fail" }));
              });
          } else {
            dispatch(addFile({ file: f }));
          }
          dispatch(addSnackbarMessage({ key: "RemoteAccess-Capture-Success" }));
        })
        .catch((e) => {
          dispatch(addSnackbarMessage({ key: "RemoteAccess-Capture-Fail" }));
        });
    }
  }, [autoSyncDocuments, dispatch, intl, job]);

  const onPeerDestroyed = useCallback(
    (_: Peer) => {
      console.log("RemoteAccess: onPeerDestroyed");
      setCallActive(false);
    },
    [setCallActive],
  );

  const onCallStreamReceived = useCallback(
    (_stream: MediaStream, _call: MediaConnection) => {
      console.log("RemoteAccess: onCallStreamReceived");
      setCallActive(true);
      if (eventState < 1) setEventState(1); //success streaming
    },
    [eventState],
  );

  useCallClient(CallClientId, {
    debug: "RemoteAccess",
    clientOptions: CallClientOptions,
    setCallActive,
    onPeerDestroyed,
    onCallStreamReceived,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className={styles.container}>
        {isSessionEnded ? (
          <RemoteSessionEnded />
        ) : isSharing ? (
          <RemoteAccessConsent onConsentRejected={handleEndCall}>
            <RemoteShareCamera
              ref={remoteCallRef}
              isCallActive={callActive}
              onEnd={handleEndCall}
              onCaptureScreen={onCapture}
            />
            <RemoteAccessButtons captureEnabled={false} onEnd={onEnd} onCapture={() => {}} />
          </RemoteAccessConsent>
        ) : (
          <>
            <RemoteViewCamera
              ref={remoteCallRef}
              isCallActive={callActive}
              onEnd={handleEndCall}
              onCaptureScreen={onCapture}
            />
            <RemoteAccessButtons
              onEnd={handleEndCall}
              onCapture={onCapture}
              onStartWork={onStartWork}
              workStarted={workStarted}
              captureEnabled={callActive}
            />
          </>
        )}
      </div>
    </div>
  );
};

async function getCaptureFilename(data: string, intl: IntlShape): Promise<string> {
  let mimeStart = data.indexOf("image/");
  if (mimeStart === -1 && data.startsWith("data:")) {
    mimeStart = 5;
  }

  if (mimeStart === -1)
    throw new Error("Unable to recognize data url: Can't find start of mimeType");

  let mimeEnd = data.indexOf(";");
  if (mimeEnd === -1) throw new Error("Unable to recognize data url: Can't find end of mimeType");

  let extension = await getExtension(data.substring(mimeStart, mimeEnd));
  return `${intl.formatMessage({ id: "remoteAccess.capturedScreenTitle" })}.${extension}`;
}

async function getExtension(mimetype: string): Promise<string> {
  return new Promise((resolve, reject) => {
    import("mime")
      .then((mimeTypes) => {
        let extension = mimeTypes.default.getExtension(mimetype);
        if (extension) resolve(extension);
        else reject(new Error(`Unable to find extension for mime-type ${mimetype}`));
      })
      .catch((e) => reject(e));
  });
}
